import * as Sentry from "@sentry/react";
import { env } from "./env";

// Lazy init. If VITE_SENTRY_DSN is empty, all Sentry calls become no-ops, so
// local dev and unconfigured environments stay quiet. Drop a DSN as a
// Cloudflare Pages env var (and rebuild) to start capturing.
//
// PHI safety: we keep tracking surface narrow on purpose.
//   - sendDefaultPii: false (don't capture IP / user agent automatically)
//   - We attach the auth user id at runtime via setUser({ id }) only — no
//     email, no name. Patient PHI must never leave the device.
//   - Replay is disabled by default. If enabled, mask all text + media.
//   - beforeSend scrubs PII patterns (email, phone) from error messages,
//     stack frame strings, URL query params, breadcrumbs, and `extra`
//     payloads — both by regex and by sensitive-key name matching.
//
// What this scrubber CANNOT catch: free-text fields containing patient PHI
// that doesn't match an obvious regex (e.g. patient names, symptoms,
// diagnoses). Callers MUST NOT pass such fields in `extra`. The scrubber
// is a safety net, not the primary defense.

// ─────────────────────────────────────────────────────────────────────────
// Scrubbing primitives
// ─────────────────────────────────────────────────────────────────────────

// Field-name patterns that should NEVER appear in a Sentry payload. Match is
// case-insensitive, anchored to the start of the key. Any matching value is
// replaced with "[REDACTED]" before send.
const SENSITIVE_KEY_RE =
  /^(email|phone|mobile|first_?name|last_?name|full_?name|patient_?name|address|street|city|zip|postal|dob|date_of_birth|ssn|patient_?id|user_?id|patient|treatment(_?id)?|medication|dose|dosage|diagnosis|condition|note|notes|internal_?note|reason|response|responses|message_?body|body|phi_)/i;

// Inline PII patterns we redact from any string value before send.
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_RE = /\b\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g;

// URL query parameters that are safe to pass through. Everything else is
// replaced with [REDACTED]. UTM params + page numbers are the only ones we
// actively use in non-authenticated routes; anything else might carry PHI.
const SAFE_QUERY_PARAMS = new Set([
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "page", "size", "tab",
]);

function scrubText(s: string): string {
  return s.replace(EMAIL_RE, "[EMAIL]").replace(PHONE_RE, "[PHONE]");
}

function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw);
    for (const k of Array.from(u.searchParams.keys())) {
      if (!SAFE_QUERY_PARAMS.has(k.toLowerCase())) {
        u.searchParams.set(k, "[REDACTED]");
      }
    }
    return u.toString();
  } catch {
    return "[INVALID-URL]";
  }
}

function redactSensitiveValue(v: unknown): unknown {
  if (v == null) return v;
  if (typeof v === "string") return scrubText(v);
  if (typeof v === "number" || typeof v === "boolean") return v;
  if (Array.isArray(v)) return v.map(redactSensitiveValue);
  if (typeof v === "object") return redactObject(v as Record<string, unknown>);
  return v;
}

function redactObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEY_RE.test(k)) {
      out[k] = "[REDACTED]";
    } else {
      out[k] = redactSensitiveValue(v);
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────────────────────────

let initialized = false;

export function initSentry() {
  if (initialized) return;
  initialized = true;
  if (!env.VITE_SENTRY_DSN) return; // silent no-op

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_RELEASE ?? undefined,
    sendDefaultPii: false,
    tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE,

    // Filter noisy / non-actionable errors before they hit our Sentry quota.
    ignoreErrors: [
      /NetworkError when attempting to fetch resource/i,
      /Load failed/i,
      /ResizeObserver loop limit exceeded/i,
      /AbortError/i,
      "Non-Error promise rejection captured",
    ],

    // Don't capture errors from browser extensions or unrelated scripts.
    denyUrls: [
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // PHI scrubber — see comments at top of file. Runs on every outbound event.
    beforeSend(event) {
      // 1. Scrub PII patterns from the top-level message + exception value/type
      if (typeof event.message === "string") event.message = scrubText(event.message);
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.value) ex.value = scrubText(ex.value);
          if (ex.type) ex.type = scrubText(ex.type);
        }
      }

      // 2. Scrub URL query params in the request payload + breadcrumb data
      if (event.request?.url) event.request.url = scrubUrl(event.request.url);
      if (event.breadcrumbs) {
        for (const b of event.breadcrumbs) {
          if (typeof b.data?.url === "string") {
            b.data.url = scrubUrl(b.data.url);
          }
          // Drop form-input breadcrumb messages entirely — they capture
          // raw user keystrokes and can contain PHI mid-typing.
          if (b.category === "ui.input") {
            b.message = "[REDACTED-INPUT]";
            if (b.data) b.data = {};
          }
          // Scrub navigation breadcrumb 'to' field for URL params
          if (typeof b.data?.to === "string") {
            b.data.to = scrubUrl(b.data.to);
          }
        }
      }

      // 3. Scrub `extra` payload by sensitive key + inline regex
      if (event.extra) {
        event.extra = redactObject(event.extra);
      }

      // 4. Scrub `contexts` (custom contexts) the same way
      if (event.contexts) {
        for (const [k, ctx] of Object.entries(event.contexts)) {
          if (ctx && typeof ctx === "object") {
            event.contexts[k] = redactObject(ctx as Record<string, unknown>);
          }
        }
      }

      // 5. Enforce user payload contains only `id` — never email or username
      if (event.user) {
        const userId = (event.user as { id?: string }).id;
        event.user = userId ? { id: userId } : {};
      }

      // 6. Scrub `tags` for PII-shaped values (rare but possible)
      if (event.tags) {
        for (const [k, v] of Object.entries(event.tags)) {
          if (typeof v === "string") event.tags[k] = scrubText(v);
        }
      }

      return event;
    },
  });
}

// Attach the auth user id once the session is known. Call setUser(null) on
// sign-out to detach. Never pass email/name/PHI.
export function setSentryUser(userId: string | null) {
  if (!initialized || !env.VITE_SENTRY_DSN) return;
  Sentry.setUser(userId ? { id: userId } : null);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
